import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Autocomplete,
  Box, 
  TextField,
  Typography, 
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  IconButton, 
  CircularProgress, 
  Snackbar, 
  Alert, 
  Breadcrumbs, 
  Link as MuiLink,
  Divider,
  DialogActions, 
  InputAdornment,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PeopleIcon from '@mui/icons-material/People';
import GitHubIcon from '@mui/icons-material/GitHub';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import './CloudDashboard.css';

const API_URL = process.env.REACT_APP_API_URL; 

const FeatureCard = ({ icon, title, description, onClick, imageSrc, titleStyle, descriptionStyle }) => (
  <Paper className="featureCard" onClick={onClick}>
    <div className="cardContent">
      <div className={`featureIcon ${title.includes('AWS') ? 'aws-logo' : 'github-logo'}`}>
        {imageSrc ? (
          <img src={imageSrc} alt={title} className="featureImage" />
        ) : icon ? (
          <div className="iconWrapper">
            {icon}
          </div>
        ) : null}
      </div>
      <div className="cardText">
        <Typography 
          variant="h6" 
          component="h2" 
          className="featureTitle"
          style={titleStyle}
        >
          {title}
        </Typography>
        <Typography 
          className="featureDescription"
          style={descriptionStyle}
        >
          {description}
        </Typography>
      </div>
    </div>
  </Paper>
);

const CloudDashboard = () => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (showUserOptions || showGitOptions) {
      // If in AWS User Management or GitHub User Management view, go back to Cloud Management
      setShowUserOptions(false);
      setShowGitOptions(false);
      setHeaderState({
        title: 'Cloud Management',
        description: 'Manage user access and cloud permissions across AWS and GitHub from a single interface.',
        subDescription: 'Streamline onboarding, offboarding, and control over DevOps environments securely.'
      });
    } else {
      // If on Cloud Management view, go to Home page
      navigate('/home');
    }
  };
  const [headerState, setHeaderState] = useState({
    title: 'Cloud Management',
    description: 'Manage user access and cloud permissions across AWS and GitHub from a single interface.',
    subDescription: 'Streamline onboarding, offboarding, and control over DevOps environments securely.'
  });
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [offboardingOpen, setOffboardingOpen] = useState(false);
  const [gitOnboardingOpen, setGitOnboardingOpen] = useState(false);
  const [gitOffboardingOpen, setGitOffboardingOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [showUserOptions, setShowUserOptions] = useState(false);
  const [showGitOptions, setShowGitOptions] = useState(false);
  const [repositories, setRepositories] = useState([]);
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [gitMembers, setGitMembers] = useState([]);
  const [selectedGitUser, setSelectedGitUser] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [repoSearchQuery, setRepoSearchQuery] = useState('');
  const [orgPlan, setOrgPlan] = useState({
    plan: 'Unknown',
    seats: 0,
    usedSeats: 0,
    availableSeats: 0,
    memberCount: 0
  });
  // Validate username format
  const validateUsername = (username) => {
    // Username must be between 1 and 64 characters
    if (!username || username.length < 1 || username.length > 64) {
      return {
        isValid: false,
        message: 'Username must be between 1 and 64 characters long.'
      };
    }
    
    // Check for allowed characters using a regular expression
    const validCharsRegex = /^[a-zA-Z0-9+=,.@-]+$/;
    if (!validCharsRegex.test(username)) {
      return {
        isValid: false,
        message: 'Username contains invalid characters. Only letters, numbers, and =+.,@- are allowed.'
      };
    }
    
    // Check for disallowed patterns
    const disallowedPatterns = [
      { pattern: /\s/, message: 'Spaces are not allowed in usernames.' },
      { pattern: /_/, message: 'Underscores (_) are not allowed in usernames.' },
      { pattern: /\//, message: 'Slashes (/) are not allowed in usernames.' },
      { pattern: /[^a-zA-Z0-9+=,.@-]/, message: 'Username contains invalid special characters.' }
    ];
    
    for (const { pattern, message } of disallowedPatterns) {
      if (pattern.test(username)) {
        return { isValid: false, message };
      }
    }
    
    return { isValid: true };
  };

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'info' // 'info', 'success', 'warning', 'error'
  });

  const showNotification = (message, type = 'info') => {
    setNotification({ open: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, open: false }));
    }, 5000);
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Fetch GitHub organization plan
  const fetchOrgPlan = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/git-onboarding/organization/plan`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrgPlan(data);
      }
    } catch (error) {
      console.error('Error fetching organization plan:', error);
    }
  };

  // Fetch GitHub repositories
  const fetchRepositories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        alert('Please log in again');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/git-onboarding/repositories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRepositories(data);
      } else {
        const error = await response.json();
        console.error('Failed to fetch repositories:', error);
      }
    } catch (error) {
      console.error('Error fetching repositories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch GitHub members
  const fetchGitMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        alert('Please log in again');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/git-onboarding/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGitMembers(data);
      } else {
        const error = await response.json();
        console.error('Failed to fetch GitHub members:', error);
      }
    } catch (error) {
      console.error('Error fetching GitHub members:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle repository selection
  const handleRepoChange = (repoName) => {
    setSelectedRepos(prevSelected => {
      if (prevSelected.includes(repoName)) {
        return prevSelected.filter(name => name !== repoName);
      } else {
        return [...prevSelected, repoName];
      }
    });
  };

  // Filter repositories based on search query
  const filteredRepositories = repositories.filter(repo => 
    repo.name.toLowerCase().includes(repoSearchQuery.toLowerCase()) ||
    (repo.description && repo.description.toLowerCase().includes(repoSearchQuery.toLowerCase()))
  );

  // Select all filtered repositories
  const selectAllFilteredRepos = () => {
    if (filteredRepositories.length === 0) return;
    
    // Check if all filtered repos are already selected
    const allSelected = filteredRepositories.every(repo => selectedRepos.includes(repo.name));
    
    if (allSelected) {
      // Deselect all filtered repos
      setSelectedRepos(prev => prev.filter(repoName => 
        !filteredRepositories.some(repo => repo.name === repoName)
      ));
    } else {
      // Select all filtered repos
      const filteredRepoNames = filteredRepositories.map(repo => repo.name);
      setSelectedRepos(prev => {
        const uniqueRepos = new Set([...prev, ...filteredRepoNames]);
        return Array.from(uniqueRepos);
      });
    }
  };

  // AWS User onboarding
  const handleSubmitOnboarding = async () => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        showNotification('Please log in again', 'error');
        setIsSubmitting(false);
        return;
      }
      
      // Validate all required fields
      const missingFields = [];
      if (!selectedAccount) missingFields.push('AWS Account');
      if (!username) missingFields.push('Username');
      if (!email) missingFields.push('Email');
      
      if (missingFields.length > 0) {
        showNotification(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
        setIsSubmitting(false);
        return;
      }
      
      // Validate username format
      const usernameValidation = validateUsername(username);
      if (!usernameValidation.isValid) {
        showNotification(usernameValidation.message, 'error');
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${API_URL}/create-iam-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email,
          username,
          accountId: selectedAccount === 'vm-1' ? '1' : '2'
        })
      });

      const result = await response.json();
      if (response.ok) {
        showNotification('User onboarded successfully!', 'success');
        setOnboardingOpen(false);
        setEmail('');
        setUsername('');
        setSelectedAccount('');
      } else {
        showNotification(`Error: ${result.message || 'Failed to onboard user'}`, 'error');
      }
    } catch (error) {
      showNotification('Error: Failed to connect to server', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // AWS User offboarding
  const handleSubmitOffboarding = async () => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        showNotification('Please log in again', 'error');
        setIsSubmitting(false);
        return;
      }
      
      // Validate all required fields
      const missingFields = [];
      if (!selectedAccount) missingFields.push('AWS Account');
      if (!username) missingFields.push('Username');
      if (!email) missingFields.push('Email');
      
      if (missingFields.length > 0) {
        showNotification(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${API_URL}/delete-iam-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email,
          username,
          accountId: selectedAccount === 'vm-1' ? '1' : '2'
        })
      });

      const result = await response.json();
      if (response.ok) {
        showNotification('User offboarded successfully!', 'success');
        setOffboardingOpen(false);
        setEmail('');
        setUsername('');
        setSelectedAccount('');
      } else {
        showNotification(`Error: ${result.message || 'Failed to offboard user'}`, 'error');
      }
    } catch (error) {
      showNotification('Error: Failed to connect to server', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // GitHub User onboarding
  const handleSubmitGitOnboarding = async () => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('auth_token');

      if (!token) {
        showNotification('Please log in again', 'error');
        setIsSubmitting(false);
        return;
      }
      
      // Validate required fields
      const missingFields = [];
      if (!username) missingFields.push('GitHub Username');
      if (!email) missingFields.push('Email');
      
      if (missingFields.length > 0) {
        showNotification(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
        setIsSubmitting(false);
        return;
      }
      
      // Validate username format
      const usernameValidation = validateUsername(username);
      if (!usernameValidation.isValid) {
        showNotification(usernameValidation.message, 'error');
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/git-onboarding/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email,
          username,
          repositories: selectedRepos
        })
      });

      const result = await response.json();
      if (response.ok) {
        if (result.data?.isExistingMember) {
          // For existing members getting repository access
          showNotification(result.message || 'Repository access granted successfully!', 'success');
        } else {
          // For new users being onboarded
          showNotification('User onboarded to GitHub successfully!', 'success');
        }
        setGitOnboardingOpen(false);
        setEmail('');
        setUsername('');
        setSelectedRepos([]);
      } else {
        // Check for specific GitHub API error about no available seats
        if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
          const seatError = result.errors.find(err => err.message && err.message.includes('No seats are available'));
          if (seatError) {
            showNotification(`Error: ${seatError.message}`, 'error');
            return;
          }
        }
        showNotification(`Error: ${result.details || result.message || 'Failed to onboard user to GitHub'}`, 'error');
      }
    } catch (error) {
      showNotification('Error: Failed to connect to server', 'error');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  // GitHub User offboarding
  const handleSubmitGitOffboarding = async () => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('auth_token');

      if (!token) {
        showNotification('Please log in again', 'error');
        setIsSubmitting(false);
        return;
      }
      
      if (!selectedGitUser) {
        showNotification('Please select a user to offboard', 'error');
        setIsSubmitting(false);
        return;
      }

      // Find the email of the selected user
      const selectedMember = gitMembers.find(member => member.login === selectedGitUser);
      
      const response = await fetch(`${API_URL}/api/git-onboarding/offboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: selectedGitUser,
          email: email // Use email if provided
        })
      });

      const result = await response.json();
      if (response.ok) {
        showNotification('User removed from GitHub organization successfully!', 'success');
        setGitOffboardingOpen(false);
        setSelectedGitUser('');
        setEmail('');
        // Refresh the members list
        fetchGitMembers();
      } else {
        showNotification(`Error: ${result.details || result.message || 'Failed to offboard user from GitHub'}`, 'error');
      }
    } catch (error) {
      showNotification('Error: Failed to connect to server', 'error');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  // Open Git onboarding dialog
  const openGitOnboarding = () => {
    resetForm();
    fetchRepositories();
    fetchOrgPlan(); // Fetch organization plan when dialog opens
    setGitOnboardingOpen(true);
  };

  // Open Git offboarding dialog
  const openGitOffboarding = () => {
    resetForm();
    setHeaderState({
      title: 'GitHub User Management',
      description: 'Manage GitHub organization access for users, including invites, removals, and role updates.',
      subDescription: 'Streamline onboarding, offboarding, and control over DevOps environments securely.'
    });
    fetchGitMembers();
    setGitOffboardingOpen(true);
  };

  // Reset form fields
  const resetForm = () => {
    setEmail('');
    setUsername('');
    setSelectedAccount('');
    setSelectedRepos([]);
    setSelectedGitUser('');
  };

  // Handle dialog close
  const handleDialogClose = (dialogType) => {
    resetForm();
    switch(dialogType) {
      case 'onboarding':
        setOnboardingOpen(false);
        break;
      case 'offboarding':
        setOffboardingOpen(false);
        break;
      case 'gitOnboarding':
        setGitOnboardingOpen(false);
        break;
      case 'gitOffboarding':
        setGitOffboardingOpen(false);
        break;
      default:
        break;
    }
  };

  // Open AWS onboarding dialog
  const openOnboarding = () => {
    resetForm();
    setHeaderState({
      title: 'AWS User Management',
      description: 'Manage AWS IAM users with streamlined onboarding and offboarding workflows.',
      subDescription: 'Grant secure access or revoke permissions to maintain control across cloud environments.'
    });
    setOnboardingOpen(true);
  };

  // Open AWS offboarding dialog
  const openOffboarding = () => {
    resetForm();
    setHeaderState({
      title: 'AWS User Management',
      description: 'Manage AWS IAM users with streamlined onboarding and offboarding workflows.',
      subDescription: 'Grant secure access or revoke permissions to maintain control across cloud environments.'
    });
    setOffboardingOpen(true);
  };

  // Import images
  const awsImage = require('../assets/images/light/aws-management-img.png');
  const githubImage = require('../assets/images/light/github-management-img.png');
  const userOnboardingImage = require('../assets/images/light/user-onboarding.png');
  const userOffboardingImage = require('../assets/images/light/user-offboarding.png');
  const gitOnboardingImage = require('../assets/images/light/user-onboarding.png');
  const gitOffboardingImage = require('../assets/images/light/user-offboarding.png');

  const handleAwsCardClick = () => {
    setHeaderState({
      title: 'AWS User Management',
      description: 'Manage AWS IAM users with streamlined onboarding and offboarding workflows.',
      subDescription: 'Grant secure access or revoke permissions to maintain control across cloud environments.'
    });
    setShowUserOptions(true);
  };

  const initialFeatures = [
    {
      title: 'AWS User Management',
      description: 'Control AWS access for your team with secure onboarding, role assignment, and deactivation tools.',
      onClick: handleAwsCardClick,
      imageSrc: awsImage
    },
    {
      title: 'GitHub User Management',
      description: 'Manage GitHub organization access for users, including invites, removals, and role updates.',
      onClick: () => {
        setHeaderState({
          title: 'GitHub User Management',
          description: 'Manage GitHub organization membership with streamlined onboarding and offboarding.',
          subDescription: 'Easily grant or revoke access to keep your developer environment secure and up to date.'
        });
        setShowGitOptions(true);
      },
      imageSrc: githubImage
    }
  ];

  const userFeatures = [
    {
      title: 'User Onboarding',
      description: 'Create new AWS IAM users and send credentials securely.',
      onClick: openOnboarding,
      imageSrc: userOnboardingImage
    },
    {
      title: 'User Offboarding',
      description: 'Remove AWS IAM users and clean up their access.',
      onClick: openOffboarding,
      imageSrc: userOffboardingImage
    }
  ];

  const gitFeatures = [
    {
      title: 'GitHub User Onboarding',
      description: 'Add users to GitHub organization and grant repository access.',
      onClick: openGitOnboarding,
      imageSrc: gitOnboardingImage,
      titleStyle: {
        color: 'var(--B6, #1674B4)',
        fontFamily: 'Proxima Nova',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        margin: '0 0 8px 0'
      },
      descriptionStyle: {
        color: 'var(--BK7, #6D6D6D)',
        fontFamily: 'Proxima Nova',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '18px'
      }
    },
    {
      title: 'GitHub User Offboarding',
      description: 'Remove users from GitHub organization and revoke their access.',
      onClick: openGitOffboarding,
      imageSrc: gitOffboardingImage,
      titleStyle: {
        color: 'var(--B6, #1674B4)',
        fontFamily: 'Proxima Nova',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        margin: '0 0 8px 0'
      },
      descriptionStyle: {
        color: 'var(--BK7, #6D6D6D)',
        fontFamily: 'Proxima Nova',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '18px'
      }
    }
  ];

  // Full page loader component
  const FullPageLoader = () => (
    <div className="full-page-loader">
      <div className="loader-content">
        <div className="spinner"></div>
        <div className="loading-text">Processing your request...</div>
      </div>
    </div>
  );

  // Notification component
  const NotificationBar = () => (
    <div className={`notification-bar ${notification.type} ${notification.open ? 'show' : ''}`}>
      <div className="notification-content">
        {notification.message}
        <button className="close-notification" onClick={closeNotification}>&times;</button>
      </div>
    </div>
  );

  return (
    <>
      {isSubmitting && <FullPageLoader />}
      <NotificationBar />
      <Box className="cloudDashboard">
      <Box className="headerContainer">
        <div className="breadcrumbContainer">
          <Link to="/home" className="breadcrumbItem">
            Dashboard
          </Link>
          <span className="breadcrumbSeparator"> &gt; </span>
          {showUserOptions || showGitOptions ? (
            <>
              <span 
                className="breadcrumbItem clickable" 
                onClick={() => {
                  setShowUserOptions(false);
                  setShowGitOptions(false);
                  setHeaderState({
                    title: 'Cloud Management',
                    description: 'Manage user access and cloud permissions across AWS and GitHub from a single interface.',
                    subDescription: 'Streamline onboarding, offboarding, and control over DevOps environments securely.'
                  });
                }}
              >
                Cloud Management
              </span>
              <span className="breadcrumbSeparator"> &gt; </span>
              <span className="breadcrumbItem active">
                {showUserOptions ? 'AWS User Management' : 'GitHub User Management'}
              </span>
            </>
          ) : (
            <span className="breadcrumbItem active">Cloud Management</span>
          )}
        </div>
        <div className="backLink" onClick={handleBack}>
          &lt; Back
        </div>
        <Box className="headerTitleSection">
          <Typography variant="h4" component="h1" className="dashboardTitle">
            {headerState.title}
          </Typography>
          <div className="headerDivider"></div>
        </Box>
        <Box className="headerDescriptionSection">
          <Typography className="dashboardSubtitle">
            <strong>{headerState.description}</strong>
            <span>{headerState.subDescription}</span>
          </Typography>
        </Box>
      </Box>

      {!showUserOptions && !showGitOptions ? (
        <div className="gridContainer">
          {initialFeatures.map((feature, index) => (
            <div className="gridItem" key={index}>
              <FeatureCard {...feature} />
            </div>
          ))}
        </div>
      ) : showUserOptions ? (
        <div className="gridContainer">
          {userFeatures.map((feature, index) => (
            <div className="gridItem" key={index}>
              <FeatureCard {...feature} />
            </div>
          ))}
        </div>
      ) : (
        <div className="gridContainer">
          {gitFeatures.map((feature, index) => (
            <div className="gridItem" key={index}>
              <FeatureCard {...feature} />
            </div>
          ))}
        </div>
      )}

      {/* AWS Onboarding Dialog */}
      <Dialog 
        open={onboardingOpen} 
        onClose={() => setOnboardingOpen(false)}
        classes={{ paper: 'dialogPaper' }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle className="dialogTitle" sx={{ m: 0, p: 2, position: 'relative' }}>
          AWS User Onboarding
          <IconButton
            aria-label="close"
            onClick={() => handleDialogClose('onboarding')}
            size="small"
            sx={{
              position: 'absolute',
              right: 36,
              top: 8,
              color: '#000000',
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem' // Reduced from default 1.5rem
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <div className="dialogTitleDivider"></div>
        </DialogTitle>
        <DialogContent>
          <Box className="dialogContent">
            <FormControl className="formField">
              <Select
                className="formSelect"
                value={selectedAccount}
                displayEmpty
                onChange={(e) => setSelectedAccount(e.target.value)}
                renderValue={selected => selected ? selected : <span style={{ color: '#757575' }}>Select AWS Account</span>}
              >
                <MenuItem value="vm-1">VM-1</MenuItem>
                <MenuItem value="vm-2">VM-2</MenuItem>
              </Select>
            </FormControl>
            <TextField
              className="formField"
              placeholder="Enter username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{
                className: 'formInput',
                'aria-label': 'Username'
              }}
            />
            <TextField
              className="formField"
              placeholder="Enter Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                className: 'formInput',
                'aria-label': 'Email'
              }}
            />
            <Box className="buttonContainer">
              <Button 
                onClick={() => handleDialogClose('onboarding')} 
                className="formButton secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitOnboarding} 
                className="formButton primary"
                variant="contained"
              >
                CREATE USER
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* AWS Offboarding Dialog */}
      <Dialog 
        id="aws-offloading-dialog"
        open={offboardingOpen} 
        onClose={() => handleDialogClose('offboarding')}
        classes={{ paper: 'dialogPaper' }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle className="dialogTitle" sx={{ m: 0, p: 2, position: 'relative' }}>
          AWS User Offboarding
          <IconButton
            aria-label="close"
            onClick={() => handleDialogClose('offboarding')}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
              color: '#000000',
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem' // Reduced from default 1.5rem
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <div className="dialogTitleDivider"></div>
        </DialogTitle>
        <DialogContent>
          <Box className="dialogContent">
            <FormControl className="formField">
              <Select
                className="formSelect"
                value={selectedAccount}
                displayEmpty
                onChange={(e) => setSelectedAccount(e.target.value)}
                renderValue={selected => selected ? selected : <span style={{ color: '#757575' }}>Select AWS Account</span>}
              >
                <MenuItem value="vm-1">VM-1</MenuItem>
                <MenuItem value="vm-2">VM-2</MenuItem>
              </Select>
            </FormControl>
            <TextField
              className="formField"
              placeholder="Enter username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{
                className: 'formInput',
                'aria-label': 'Username'
              }}
            />
            <TextField
              className="formField"
              placeholder="Enter Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                className: 'formInput',
                'aria-label': 'Email'
              }}
            />
            <Box className="buttonContainer">
              <Button 
                onClick={() => handleDialogClose('offboarding')} 
                className="formButton secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitOffboarding} 
                className="formButton primary"
              >
                REMOVE USER
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* GitHub Onboarding Dialog */}
      <Dialog 
        open={gitOnboardingOpen} 
        onClose={() => handleDialogClose('gitOnboarding')}
        className="githubDialog"
        maxWidth={false}
      >
        <DialogTitle className="dialogTitle" sx={{ m: 0, p: 2, position: 'relative' }}>
          GitHub User Onboarding
          <IconButton
            aria-label="close"
            onClick={() => handleDialogClose('gitOnboarding')}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
              color: '#000000',
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem' // Reduced from default 1.5rem
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <div className="dialogTitleDivider"></div>
          {orgPlan.plan !== 'Unknown' && (
            <Typography variant="body2" sx={{
              fontSize: '0.875rem',
              color: orgPlan.availableSeats > 0 ? 'success.main' : 'error.main',
              mt: 1,
              fontWeight: 500
            }}>
              {orgPlan.availableSeats > 0 
                ? `${orgPlan.availableSeats} of ${orgPlan.seats} seats available`
                : 'No seats available'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box className="loadingContainer">
              <CircularProgress />
            </Box>
          ) : (
            <Box className="dialogContent">
              <TextField
                className="formField"
                placeholder="Enter GitHub username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                InputProps={{
                  className: 'formInput',
                  'aria-label': 'GitHub Username'
                }}
                InputLabelProps={{
                  className: 'formLabel'
                }}
              />
              <TextField
                className="formField"
                placeholder="Enter Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  className: 'formInput',
                  'aria-label': 'Email'
                }}
                InputLabelProps={{
                  className: 'formLabel'
                }}
              />

              <Box className="repoContainer">
                <div className="repoHeader">
                  <div className="searchContainer">
                    <TextField
                      className="repoSearch formField"
                      placeholder="Search Repositories"
                      value={repoSearchQuery}
                      onChange={(e) => setRepoSearchQuery(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" style={{ marginRight: '12px', position: 'absolute', right: 0 }}>
                            <SearchIcon 
                              style={{
                                width: '18.486px',
                                height: '18.487px',
                                flexShrink: 0,
                                fill: 'var(--B8, #004F85)'
                              }}
                            />
                          </InputAdornment>
                        ),
                        className: 'formInput',
                        disableUnderline: true,
                        style: {
                          padding: '8px 36px 8px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                          width: '100%',
                          position: 'relative',
                          paddingRight: '40px'  /* Increased right padding to prevent text overlap */
                        }
                      }}
                      InputLabelProps={{
                        className: 'formLabel'
                      }}
                    />
                  </div>
                  <div className="selectionControls">
                    <button 
                      className={`selectAllButton ${selectedRepos.length === filteredRepositories.length ? 'selected' : ''}`}
                      onClick={selectAllFilteredRepos}
                    >
                      {selectedRepos.length === filteredRepositories.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="selectedCount">
                      {selectedRepos.length} {selectedRepos.length === 1 ? 'repository' : 'repositories'} selected
                    </span>
                  </div>
                </div>
                <Paper className="repoList">
                  {filteredRepositories.length === 0 && repoSearchQuery !== '' ? (
                    <Typography variant="body2" className="noResults">
                      No repositories matching "{repoSearchQuery}"
                    </Typography>
                  ) : (
                    <FormGroup>
                      {filteredRepositories.map((repo) => (
                        <FormControlLabel
                          key={repo.id}
                          control={
                            <Checkbox 
                              checked={selectedRepos.includes(repo.name)}
                              onChange={() => handleRepoChange(repo.name)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" component="span" className="repoName">
                                {repo.name}
                              </Typography>
                              {repo.description && (
                                <Typography variant="caption" component="div" className="repoDescription">
                                  {repo.description.length > 60 
                                    ? `${repo.description.substring(0, 60)}...` 
                                    : repo.description}
                                </Typography>
                              )}
                            </Box>
                          }
                          className="repoItem"
                        />
                      ))}
                    </FormGroup>
                  )}
                </Paper>
              </Box>
              <Box className="buttonContainer">
                <Box className="buttonGroup">
                  <Button 
                    onClick={() => handleDialogClose('gitOnboarding')} 
                    className="formButton secondary"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSubmitGitOnboarding} 
                    className="formButton primary"
                    variant="contained"
                  >
                    Onboard User
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* GitHub Offboarding Dialog */}
      <Dialog 
        open={gitOffboardingOpen} 
        onClose={() => handleDialogClose('gitOffboarding')}
        classes={{ paper: 'dialogPaper githubDialog' }}
        id="github-offloading-dialog"
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle className="dialogTitle" sx={{ m: 0, p: '1.5rem 3rem 1rem', position: 'relative' }}>
          GitHub User Offboarding
          <div className="dialogTitleDivider"></div>
          <IconButton
            aria-label="close"
            onClick={() => handleDialogClose('gitOffboarding')}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
              color: '#000000',
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem' // Reduced from default 1.5rem
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box className="loadingContainer">
              <CircularProgress />
            </Box>
          ) : (
            <Box className="dialogContent">
              <FormControl className="formField" fullWidth sx={{ mb: 3 }}>
                <Autocomplete
                  options={gitMembers}
                  getOptionLabel={(option) => option.login}
                  value={gitMembers.find(member => member.login === selectedGitUser) || null}
                  onChange={(event, newValue) => {
                    setSelectedGitUser(newValue ? newValue.login : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select user"
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '40px',
                          padding: '0 12px',
                          '& fieldset': {
                            border: '1px solid var(--BK3, #D9D9D9)',
                            boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25)',
                          },
                        },
                      }}
                    />
                  )}
                  classes={{
                    paper: 'githubUserDropdown',
                    listbox: 'githubUserDropdown',
                    option: 'githubUserDropdown',
                  }}
                  sx={{
                    width: '500px',
                    '& .MuiAutocomplete-clearIndicator': {
                      display: 'none',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                  style={{ width: '500px' }}
                />
              </FormControl>
              
              <TextField
                placeholder="Enter Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email for notification (optional)"
                sx={{
                  width: '100%',
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    padding: '6px 12px',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    borderRadius: '4px',
                    border: '1px solid var(--BK3, #D9D9D9)',
                    background: 'var(--BK1, #FFF)',
                    boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25)',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                    marginTop: '4px',
                    color: 'var(--BK7, #6D6D6D)',
                    fontSize: '12px',
                  },
                  mb: 2
                }}
                helperText="If provided, a notification email will be sent"
              />
              
              <Box className="buttonContainer">
                <Button 
                  onClick={() => handleDialogClose('gitOffboarding')} 
                  className="formButton secondary"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitGitOffboarding}
                  className="formButton primary"
                >
                  Offboard User
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      </Box>
    </>
  );
};

export default CloudDashboard;